/**
 * chavat-delivery-popup.js
 * לוגיקת פופאפ בחירת עיר + תאריך משלוח - חוות הפרא
 *
 * תלוי בקובץ chavat-city-regions.json (asset) ובקונפיג שמוזרק מ-snippets/chavat-delivery-popup.liquid
 * דרך window.chavatDeliveryConfig
 *
 * ה-API הציבורי היחיד שקוד אחר בתמה צריך להכיר:
 *
 *   ChavatDeliveryPopup.ensureSelection(callback)
 *     - אם יש כבר בחירה תקפה בסשן הנוכחי -> קורא ל-callback(selection) מיידית, בלי להציג UI
 *     - אחרת -> פותח את הפופאפ; אחרי אישור הלקוח קורא ל-callback(selection)
 *     - selection = { city, region, regionName, dateISO, dateLabel }
 *
 *   ChavatDeliveryPopup.getSelection() -> selection | null
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'chavat_delivery_selection_v1';
  var UNMATCHED_LOG_KEY = 'chavat_unmatched_cities_v1';
  var cityRegionsCache = null;
  var pendingCallback = null;

  /**
   * תאריכים חסומים למשלוח (כולל שני הקצוות), בפורמט YYYY-MM-DD.
   * כל תאריך משלוח שנופל בטווח לא יוצג בפופאפ, ובחירה שמורה עם תאריך כזה תתאפס.
   * כדי לחסום שבוע נוסף - להוסיף שורה. כדי לבטל חסימה - למחוק את השורה.
   */
  var BLOCKED_DATE_RANGES = [
    { from: '2026-10-22', to: '2026-10-31' }
  ];

  function isBlockedDate(dateISO) {
    var cfg = window.chavatDeliveryConfig || {};
    var ranges = BLOCKED_DATE_RANGES.concat(cfg.blockedRanges || []);
    return ranges.some(function (r) {
      return dateISO >= r.from && dateISO <= r.to;
    });
  }

  function normalize(str) {
    if (!str) return '';
    return str
      .trim()
      .replace(/["'`׳״]/g, '')
      .replace(/[-־]/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function loadCityRegions() {
    if (cityRegionsCache) return Promise.resolve(cityRegionsCache);
    var url = window.chavatDeliveryConfig && window.chavatDeliveryConfig.cityRegionsUrl;
    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var index = {};
        var info = {};
        Object.keys(data).forEach(function (city) {
          var parsed = parseRegionValue(data[city]);
          info[city] = parsed;
          index[normalize(city)] = { name: city, region: parsed.region, remote: parsed.remote };
        });
        cityRegionsCache = { raw: data, info: info, index: index, cities: Object.keys(data) };
        return cityRegionsCache;
      });
  }

  // ערך בקובץ הערים: "north" או "north-remote" (remote = דמי משלוח קבועים לאזור מרוחק)
  function parseRegionValue(value) {
    var v = String(value || '');
    var remote = /-remote$/.test(v);
    return { region: v.replace(/-remote$/, ''), remote: remote };
  }

  function findExactRegion(cache, typedCity) {
    var n = normalize(typedCity);
    return cache.index[n] || null;
  }

  function findSuggestions(cache, typedCity, limit) {
    var n = normalize(typedCity);
    if (!n) return [];
    var startsWith = [];
    var contains = [];
    cache.cities.forEach(function (city) {
      var nc = normalize(city);
      if (nc === n) return;
      if (nc.indexOf(n) === 0) startsWith.push(city);
      else if (nc.indexOf(n) !== -1) contains.push(city);
    });
    return startsWith.concat(contains).slice(0, limit || 8);
  }

  function logUnmatchedCity(typedCity) {
    try {
      var log = JSON.parse(localStorage.getItem(UNMATCHED_LOG_KEY) || '[]');
      log.push({ city: typedCity, at: new Date().toISOString() });
      if (log.length > 200) log = log.slice(log.length - 200);
      localStorage.setItem(UNMATCHED_LOG_KEY, JSON.stringify(log));
    } catch (e) { /* אחסון לא זמין - לא קריטי */ }
  }

  function addDays(date, days) {
    var d = new Date(date.getTime());
    d.setDate(d.getDate() + days);
    return d;
  }

  function formatDateHeb(date) {
    var dd = String(date.getDate()).padStart(2, '0');
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var yyyy = date.getFullYear();
    return dd + '/' + mm + '/' + yyyy;
  }

  function toISODateLocal(date) {
    var yyyy = date.getFullYear();
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var dd = String(date.getDate()).padStart(2, '0');
    return yyyy + '-' + mm + '-' + dd;
  }

  var WEEKDAY_NAMES = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'יום שבת'];

  function hoursForDay(date) {
    var cfg = window.chavatDeliveryConfig;
    var iso = toISODateLocal(date);
    if (cfg.hoursOverrides && cfg.hoursOverrides[iso]) return cfg.hoursOverrides[iso];
    if (date.getDay() === 5) return cfg.fridayHours || cfg.deliveryHours || '8:00-19:00';
    return cfg.deliveryHours || '8:00-19:00';
  }

  /**
   * מחזור משלוחים לאזור:
   * - everyWeeks: אורך המחזור בשבועות (אם חסר: weekly = 1, biweekly = 2)
   * - anchors: תאריכי המשלוח בתוך המחזור הראשון (אם חסר: anchor יחיד)
   * דוגמה: everyWeeks: 3, anchors: ['2026-10-23', '2026-10-30'] = שבועיים משלוח, שבוע הפסקה, וחוזר חלילה
   */
  function getDatesForRegion(regionKey) {
    var cfg = window.chavatDeliveryConfig;
    var region = cfg.regions[regionKey];
    if (!region) return [];

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var horizonEnd = addDays(today, cfg.horizonWeeks * 7);

    var everyWeeks = region.everyWeeks || (region.frequency === 'biweekly' ? 2 : 1);
    var stepDays = everyWeeks * 7;
    var anchors = region.anchors || [region.anchor];

    var dates = [];
    anchors.forEach(function (anchorISO) {
      var cursor = new Date(anchorISO + 'T00:00:00');
      while (cursor < today) {
        cursor = addDays(cursor, stepDays);
      }
      while (cursor <= horizonEnd) {
        dates.push({
          dateISO: toISODateLocal(cursor),
          label: WEEKDAY_NAMES[cursor.getDay()] + ' ' + formatDateHeb(cursor),
          hours: hoursForDay(cursor)
        });
        cursor = addDays(cursor, stepDays);
      }
    });
    dates.sort(function (a, b) { return a.dateISO < b.dateISO ? -1 : a.dateISO > b.dateISO ? 1 : 0; });

    return filterPastCutoff(applyOverrides(dates, region.overrides)).filter(function (d) {
      return !isBlockedDate(d.dateISO);
    });
  }

  // בודק שתאריך שמור עדיין שייך למחזור הנוכחי של האזור (מאפס בחירות ישנות אחרי שינוי מחזור)
  function isCycleDate(regionKey, dateISO) {
    var cfg = window.chavatDeliveryConfig || {};
    var region = cfg.regions && cfg.regions[regionKey];
    if (!region) return true;
    var overrides = region.overrides || [];
    if (overrides.some(function (o) { return o.to === dateISO; })) return true;
    var everyWeeks = region.everyWeeks || (region.frequency === 'biweekly' ? 2 : 1);
    var stepDays = everyWeeks * 7;
    var target = new Date(dateISO + 'T12:00:00');
    var anchors = region.anchors || [region.anchor];
    return anchors.some(function (a) {
      var diff = Math.round((target - new Date(a + 'T12:00:00')) / 86400000);
      return diff >= 0 && diff % stepDays === 0;
    });
  }

  /**
   * שעת סגירת הזמנות: 13:00 ביום שלפני המשלוח, לפי שעון ישראל
   * (מחושב לפי Asia/Jerusalem ולא לפי השעון של המכשיר של הלקוח).
   */
  var CUTOFF_HOUR = 13;

  function israelNowParts() {
    try {
      var fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      });
      var p = {};
      fmt.formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      return { dateISO: p.year + '-' + p.month + '-' + p.day, hour: Number(p.hour) };
    } catch (e) {
      var n = new Date();
      return { dateISO: toISODateLocal(n), hour: n.getHours() };
    }
  }

  // true = כבר אי אפשר להזמין לתאריך הזה
  function isPastCutoff(dateISO) {
    var dayBefore = toISODateLocal(addDays(new Date(dateISO + 'T12:00:00'), -1));
    var now = israelNowParts();
    if (now.dateISO > dayBefore) return true;
    if (now.dateISO === dayBefore && now.hour >= CUTOFF_HOUR) return true;
    return false;
  }

  function filterPastCutoff(dates) {
    return dates.filter(function (d) {
      return !isPastCutoff(d.dateISO);
    });
  }

  function applyOverrides(dates, overrides) {
    if (!overrides || !overrides.length) return dates;
    var map = {};
    overrides.forEach(function (o) { map[o.from] = o.to; });
    return dates.map(function (d) {
      if (!map[d.dateISO]) return d;
      var newDate = new Date(map[d.dateISO] + 'T00:00:00');
      return {
        dateISO: map[d.dateISO],
        label: WEEKDAY_NAMES[newDate.getDay()] + ' ' + formatDateHeb(newDate),
        hours: hoursForDay(newDate)
      };
    });
  }

  function saveSelection(selection) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (e) { /* ignore */ }
  }

  function getSelection() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var selection = JSON.parse(raw);
      if (selection && selection.dateISO) {
        // בחירה שמורה פוקעת ברגע שעברה שעת הסגירה (13:00 יום לפני המשלוח)
        if (isPastCutoff(selection.dateISO) || isBlockedDate(selection.dateISO) || !isCycleDate(selection.region, selection.dateISO)) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      }
      return selection;
    } catch (e) {
      return null;
    }
  }

  var modalEl = null;

  function getModal() {
    if (!modalEl) modalEl = document.getElementById('chavat-delivery-modal');
    return modalEl;
  }

  function openModal() {
    var modal = getModal();
    if (!modal) return;
    modal.classList.add('is-open');
    document.body.classList.add('chavat-modal-open');
    var input = modal.querySelector('.chavat-city-input');
    if (input) setTimeout(function () { input.focus(); }, 50);
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('chavat-modal-open');
  }

  function resetModalToSearchStep() {
    var modal = getModal();
    if (!modal) return;
    modal.querySelector('.chavat-step-dates').hidden = true;
    modal.querySelector('.chavat-step-search').hidden = false;
    modal.querySelector('.chavat-suggestions').innerHTML = '';
    modal.querySelector('.chavat-error').hidden = true;
    modal.querySelector('.chavat-city-input').value = '';
  }

  function renderDateCards(regionKey, cityName, isRemote) {
    var modal = getModal();
    var cfg = window.chavatDeliveryConfig;
    var region = cfg.regions[regionKey];
    var dates = getDatesForRegion(regionKey);

    modal.querySelector('.chavat-step-search').hidden = true;
    var stepDates = modal.querySelector('.chavat-step-dates');
    stepDates.hidden = false;

    modal.querySelector('.chavat-chosen-city').textContent = cityName;

    var remoteNotice = modal.querySelector('.chavat-remote-notice');
    if (remoteNotice) remoteNotice.hidden = !isRemote;

    var wrap = modal.querySelector('.chavat-date-cards');
    wrap.innerHTML = '';

    if (!dates.length) {
      wrap.innerHTML = '<p class="chavat-error" style="display:block">מצטערים, אין כרגע תאריכי משלוח פתוחים לאזור הזה. נסו שוב בקרוב.</p>';
    }

    dates.forEach(function (d) {
      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'chavat-date-card';
      card.setAttribute('data-date-iso', d.dateISO);
      card.innerHTML =
        '<span class="chavat-date-card-line">קו ' + region.name + '</span>' +
        '<span class="chavat-date-card-line">' + d.label + '</span>' +
        '<span class="chavat-date-card-line chavat-date-card-hours">' + (d.hours || '') + '</span>';
      card.addEventListener('click', function () {
        wrap.querySelectorAll('.chavat-date-card').forEach(function (c) { c.classList.remove('is-selected'); });
        card.classList.add('is-selected');
        var confirmBtn = modal.querySelector('.chavat-confirm-btn');
        confirmBtn.hidden = false;
        confirmBtn.setAttribute('data-date-iso', d.dateISO);
        confirmBtn.setAttribute('data-date-label', d.label);
      });
      wrap.appendChild(card);
    });
  }

  function handleSearch(cache, typedCity) {
    var modal = getModal();
    var errorEl = modal.querySelector('.chavat-error');
    var suggestionsEl = modal.querySelector('.chavat-suggestions');

    var exact = findExactRegion(cache, typedCity);
    if (exact) {
      errorEl.hidden = true;
      suggestionsEl.innerHTML = '';
      renderDateCards(exact.region, exact.name, exact.remote);
      return;
    }

    var suggestions = findSuggestions(cache, typedCity, 8);
    if (!typedCity.trim()) {
      suggestionsEl.innerHTML = '';
      errorEl.hidden = true;
      return;
    }

    if (suggestions.length) {
      suggestionsEl.innerHTML = '';
      suggestions.forEach(function (city) {
        var item = document.createElement('button');
        item.type = 'button';
        item.className = 'chavat-suggestion-item';
        item.textContent = city;
        item.addEventListener('click', function () {
          var parsed = cache.info[city];
          errorEl.hidden = true;
          suggestionsEl.innerHTML = '';
          modal.querySelector('.chavat-city-input').value = city;
          renderDateCards(parsed.region, city, parsed.remote);
        });
        suggestionsEl.appendChild(item);
      });
      errorEl.hidden = true;
    } else {
      suggestionsEl.innerHTML = '';
      errorEl.hidden = false;
      errorEl.textContent = 'מצטערים, אנחנו עדיין לא מגיעים ל"' + typedCity + '". נעדכן כשנרחיב את אזור החלוקה.';
      logUnmatchedCity(typedCity);
    }
  }

  function wireModalEvents(cache) {
    var modal = getModal();
    if (!modal || modal._wired) return;
    modal._wired = true;

    var input = modal.querySelector('.chavat-city-input');
    var searchBtn = modal.querySelector('.chavat-search-btn');
    var backBtn = modal.querySelector('.chavat-back-btn');
    var closeBtn = modal.querySelector('.chavat-close-btn');
    var confirmBtn = modal.querySelector('.chavat-confirm-btn');

    var runSearch = function () { handleSearch(cache, input.value); };

    searchBtn.addEventListener('click', runSearch);
    input.addEventListener('input', runSearch);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); runSearch(); }
    });

    backBtn.addEventListener('click', resetModalToSearchStep);
    closeBtn.addEventListener('click', closeModal);
    modal.querySelector('.chavat-modal-overlay').addEventListener('click', closeModal);

    confirmBtn.addEventListener('click', function () {
      var cityName = modal.querySelector('.chavat-chosen-city').textContent;
      var exact = findExactRegion(cache, cityName);
      if (!exact) return;
      var chosenISO = confirmBtn.getAttribute('data-date-iso');
      // הגנה: אם הלקוח השאיר את הפופאפ פתוח ועברה שעת הסגירה בינתיים
      if (!chosenISO || isPastCutoff(chosenISO)) {
        renderDateCards(exact.region, exact.name, exact.remote);
        confirmBtn.hidden = true;
        return;
      }
      var cfg = window.chavatDeliveryConfig;
      var region = cfg.regions[exact.region];
      var selection = {
        city: exact.name,
        region: exact.region,
        regionName: region.name,
        remote: !!exact.remote,
        dateISO: chosenISO,
        dateLabel: confirmBtn.getAttribute('data-date-label')
      };
      saveSelection(selection);
      closeModal();
      if (typeof pendingCallback === 'function') {
        var cb = pendingCallback;
        pendingCallback = null;
        // לאזור מרוחק: מוצר דמי המשלוח נכנס לעגלה לפני המוצר, כדי שיופיע מיד בעגלה
        ensureRemoteFeePresent().then(function () { cb(selection); });
      }
    });
  }

  function ensureSelection(callback) {
    var existing = getSelection();
    if (existing) {
      ensureRemoteFeePresent().then(function () { callback(existing); });
      return;
    }
    pendingCallback = callback;
    loadCityRegions().then(function (cache) {
      wireModalEvents(cache);
      resetModalToSearchStep();
      openModal();
    });
  }

  window.ChavatDeliveryPopup = {
    ensureSelection: ensureSelection,
    getSelection: getSelection
  };

  function setHiddenInput(form, name, value) {
    var input = form.querySelector('input[name="' + name.replace(/"/g, '\\"') + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function injectSelectionIntoForm(button, selection) {
    var form = button.closest('form');
    if (!form) return;
    var noteText = 'עיר משלוח: ' + selection.city + ' | תאריך משלוח: ' + selection.dateLabel + ' (' + selection.regionName + ')';
    setHiddenInput(form, 'note', noteText);
  }

  function isAddToCartForm(form) {
    if (!form) return false;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) return true;
    return !!form.querySelector('button[name="add"], input[name="add"]');
  }

  function findAddToCartButton(target) {
    var button = target.closest('button[type="submit"], input[type="submit"], button[name="add"]');
    if (!button) return null;
    var form = button.closest('form');
    if (!isAddToCartForm(form)) return null;
    return button;
  }

  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isAddToCartForm(form)) return;

    if (form.dataset.chavatBypass === '1') return;

    var existing = getSelection();
    var submitter = event.submitter || form.querySelector('button[type="submit"], button[name="add"]');

    if (existing) {
      if (submitter) injectSelectionIntoForm(submitter, existing);
      ensureRemoteFeePresent();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    ensureSelection(function (selection) {
      if (submitter) injectSelectionIntoForm(submitter, selection);
      form.dataset.chavatBypass = '1';
      if (form.requestSubmit) {
        form.requestSubmit(submitter || undefined);
      } else {
        form.submit();
      }
      delete form.dataset.chavatBypass;
    });
  }, true);

  window.addEventListener('click', function (event) {
    var button = findAddToCartButton(event.target);
    if (!button) return;
    if (button.dataset.chavatBypass === '1') return;

    var existing = getSelection();
    if (existing) {
      injectSelectionIntoForm(button, existing);
      ensureRemoteFeePresent();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    ensureSelection(function (selection) {
      injectSelectionIntoForm(button, selection);
      button.dataset.chavatBypass = '1';
      button.click();
      delete button.dataset.chavatBypass;
    });
  }, true);

  function navigateToCheckoutWithCity(city) {
    var url = new URL('/checkout', window.location.origin);
    url.searchParams.set('checkout[shipping_address][city]', city);
    window.location.href = url.toString();
  }

  window.addEventListener('click', function (event) {
    var target = event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (!target) return;
    if (target.dataset.chavatBypass === '1') return;

    var existing = getSelection();
    if (existing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var navigated = false;
      var go = function () {
        if (navigated) return;
        navigated = true;
        navigateToCheckoutWithCity(existing.city);
      };
      setTimeout(go, 4000); // רשת ביטחון - לא להשאיר לקוח תקוע
      syncRemoteFee().then(go, go);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    ensureSelection(function (selection) {
      var noteText = 'עיר משלוח: ' + selection.city + ' | תאריך משלוח: ' + selection.dateLabel + ' (' + selection.regionName + ')';
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ note: noteText })
      }).then(function () {
        return syncRemoteFee();
      }).then(function () {
        navigateToCheckoutWithCity(selection.city);
      }).catch(function () {
        navigateToCheckoutWithCity(selection.city);
      });
    });
  }, true);

  /**
   * דמי משלוח לאזור מרוחק:
   * ללקוח מאזור מרוחק מוסיפים תמיד את מוצר "משלוח – אזור מרוחק" (59.99, משקל 500 ק"ג).
   * בהגדרות המשלוח: עגלה מעל 500 ק"ג מקבלת רק משלוח חינם, כך שהלקוח משלם 59.99 בדיוק - פעם אחת.
   */
  function syncRemoteFee() {
    var cfg = window.chavatDeliveryConfig || {};
    var fee = cfg.remoteFee;
    if (!fee || !fee.variantId) return Promise.resolve();
    var selection = getSelection();
    var feeVariantId = Number(fee.variantId);

    var isRemote = false;
    var remoteCheck = selection
      ? loadCityRegions().then(function (cache) {
          var exact = findExactRegion(cache, selection.city);
          isRemote = !!(exact && exact.remote);
        })
      : Promise.resolve();

    return remoteCheck
      .then(function () { return fetch('/cart.js'); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var feeQty = 0;
        var feeLineTotal = 0;
        var otherItems = 0;
        (cart.items || []).forEach(function (item) {
          if (Number(item.variant_id) === feeVariantId) {
            feeQty += item.quantity;
            feeLineTotal += item.final_line_price;
          } else {
            otherItems += item.quantity;
          }
        });
        var shouldHave = !!(selection && isRemote && otherItems > 0);
        var wantedQty = shouldHave ? 1 : 0;
        if (feeQty === wantedQty) return;
        var updates = {};
        updates[feeVariantId] = wantedQty;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ updates: updates })
        });
      })
      .catch(function () { /* לא קריטי - ננסה שוב במעבר לתשלום */ });
  }

  /**
   * מוסיף את מוצר דמי המשלוח לעגלה אם הלקוח מאזור מרוחק והמוצר עדיין לא בעגלה.
   * נקרא לפני הוספת מוצר לעגלה (בלי לבדוק אם יש מוצרים אחרים - הם בדרך).
   * עגלה שנשארת רק עם דמי המשלוח מתנקה ב-syncRemoteFee בטעינת הדף הבאה.
   */
  function ensureRemoteFeePresent() {
    var cfg = window.chavatDeliveryConfig || {};
    var fee = cfg.remoteFee;
    var selection = getSelection();
    if (!fee || !fee.variantId || !selection) return Promise.resolve();
    var feeVariantId = Number(fee.variantId);

    var remoteCheck = (typeof selection.remote === 'boolean')
      ? Promise.resolve(selection.remote)
      : loadCityRegions().then(function (cache) {
          var exact = findExactRegion(cache, selection.city);
          return !!(exact && exact.remote);
        });

    return remoteCheck
      .then(function (isRemote) {
        if (!isRemote) return;
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var hasFee = (cart.items || []).some(function (item) {
              return Number(item.variant_id) === feeVariantId;
            });
            if (hasFee) return;
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({ items: [{ id: feeVariantId, quantity: 1 }] })
            });
          });
      })
      .catch(function () { /* לא קריטי - יתווסף במעבר לתשלום */ });
  }

  window.ChavatDeliveryPopup.syncRemoteFee = syncRemoteFee;
  window.ChavatDeliveryPopup.ensureRemoteFeePresent = ensureRemoteFeePresent;

  function reconcileCartNote() {
    var selection = getSelection();
    if (!selection) return;
    var expectedNote = 'עיר משלוח: ' + selection.city + ' | תאריך משלוח: ' + selection.dateLabel + ' (' + selection.regionName + ')';
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;
        if (cart.note === expectedNote) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ note: expectedNote })
        });
      })
      .catch(function () { /* לא קריטי - ננסה שוב בטעינת הדף הבאה */ })
      .then(function () { return syncRemoteFee(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileCartNote);
  } else {
    reconcileCartNote();
  }
})();
